import { router } from "@inertiajs/react"
import { Bot, Search, Star, User } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { partnerAccountPath, partnerSearchResultsPath } from "@/routes"

interface SearchResult {
  id: string
  record_type: "Account" | "Agent" | "User"
  name: string
  email: string | null
  account_name: string | null
  account_uid?: string | null
}

const typeIcons = {
  Account: Star,
  Agent: Bot,
  User: User,
} as const

export function PartnerSearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset when the parent closes the dialog
      setQuery("")
      setResults([])
      setIsLoading(false)
      return
    }

    const timeoutId = setTimeout(() => {
      if (query.trim().length < 2) {
        setResults([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      fetch(`${partnerSearchResultsPath()}?q=${encodeURIComponent(query)}`)
        .then((res) => res.json())
        .then((data) => {
          setResults(data.results ?? [])
          setIsLoading(false)
        })
        .catch(() => {
          setResults([])
          setIsLoading(false)
        })
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query, open])

  const navigate = useCallback(
    (result: SearchResult) => {
      onOpenChange(false)
      switch (result.record_type) {
        case "Account":
          router.visit(partnerAccountPath({ id: result.id }))
          break
        case "Agent":
          // IMP-511: the Agents page is retired — the agent's home is its
          // account's Billing tab (the jobs list).
          if (result.account_uid) {
            router.visit(
              `${partnerAccountPath({ id: result.account_uid })}?tab=billing`,
            )
          }
          break
        case "User":
          router.visit(partnerSearchResultsPath({ q: result.name }))
          break
      }
    },
    [onOpenChange],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-[350px] overflow-hidden p-0"
        showCloseButton={false}
      >
        <Command className="bg-background rounded-lg border-none">
          <CommandInput
            placeholder="Search for anything"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="bg-background">
            {results.length === 0 && query.trim().length >= 2 && !isLoading ? (
              <CommandEmpty>No results found.</CommandEmpty>
            ) : results.length === 0 ? (
              <CommandEmpty>
                {isLoading ? "Searching..." : "Start typing to search..."}
              </CommandEmpty>
            ) : (
              <CommandGroup heading="Results">
                {results.map((result) => {
                  const Icon = typeIcons[result.record_type] ?? Search
                  return (
                    <CommandItem
                      key={`${result.record_type}-${result.id}`}
                      value={`${result.name} ${result.email ?? ""} ${result.account_name ?? ""}`}
                      onSelect={() => navigate(result)}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 size-4" />
                      <div className="flex flex-col">
                        <span className="text-foreground font-semibold">
                          {result.name}
                        </span>
                        {result.email && (
                          <span className="text-muted-foreground text-sm">
                            {result.email}
                          </span>
                        )}
                        {result.account_name && (
                          <span className="text-muted-foreground text-sm">
                            {result.account_name}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
